import {useState} from "react";
import type {Product} from "@corporatesclub/shared";
import {toggleWishlist, productImageFromUrl} from "@corporatesclub/shared";
import {Link} from "react-router-dom";
import {DsBadge, DsButton} from "@corporatesclub/ui";
import {useI18n} from "./I18nProvider";
import {useAddToCart} from "@/hooks/useAddToCart";
import {useSession} from "./SessionProvider";
import {useShopSite} from "./ShopSiteProvider";
import {ProductPrice} from "./ProductPrice";

type Props = {
  product: Product;
  showWishlist?: boolean;
  compact?: boolean;
  onNavigate?: () => void;
};

export function ProductCard({product, showWishlist = false, compact = false, onNavigate}: Props) {
  const {t} = useI18n();
  const {user} = useSession();
  const {status, ready: siteReady} = useShopSite();
  const {add, isAdding, canAddToCart} = useAddToCart();
  const [wish, setWish] = useState(!!product.inWishlist);
  const pricesHidden = product.pricesHidden || (siteReady && !user?.authenticated && !status.guestPricesVisible);
  const canOrder = user?.authenticated || (siteReady && status.guestOrdersAllowed);
  const showAdd = product.inStock && canAddToCart && canOrder;
  async function onAdd(e: React.MouseEvent) {
    e.preventDefault();
    if(!product.inStock || !canAddToCart) return;
    try {
      await add({
        id_produs: product.id,
        _pret: product.pretUnit,
        cantitate: 1,
        _locatie: product.locatie,
        product: {
          brand: product.brand,
          nume: product.nume,
          imagine: product.imagine,
          pretEuro: product.pretEuro,
          pretLei: product.pretLei,
          pretUnit: product.pretUnit
        }
      });
    } catch(err) {
      console.error(err);
    }
  }
  async function onWish(e: React.MouseEvent) {
    e.preventDefault();
    const r = await toggleWishlist(product.id);
    setWish(!wish);
    window.dispatchEvent(new CustomEvent("wishlist-updated", {detail: r.count}));
  }
  const cardCls = `cc-productCard bx-productCard${compact ? " cc-productCard--compact" : ""}${product.inStock ? "" : " cc-productCard--soldOut"}`;
  const addLabel = isAdding(product.id) ? "..." : t("Add to cart");
  return (
    <article className={cardCls} id={`prds${product.id}`}>
      {product.discountPercent > 0 && !pricesHidden ? (
        <DsBadge variant="sale" className="cc-productCard__badge">-{product.discountPercent}%</DsBadge>
      ) : null}
      <div className="cc-productCard__media">
        {showWishlist ? (
          <button type="button" className={`cc-productCard__wish${wish ? " isActive" : ""}`} onClick={onWish} title={t("Wishlist")} aria-label={t("Wishlist")}>
            <i className={`fa ${wish ? "fa-heart" : "fa-heart-o"}`} aria-hidden="true" />
          </button>
        ) : null}
        <Link to={product.url} className="cc-productCard__imgLink" onClick={onNavigate}>
          <img className="cc-productCard__img" src={productImageFromUrl(product.imagine)} alt={product.nume} loading="lazy" />
        </Link>
        {showAdd ? (
          <DsButton variant="primary" size="sm" className={`cc-productCard__quickAdd${compact ? " cc-productCard__quickAdd--compact" : ""}`} onClick={onAdd} disabled={isAdding(product.id)}>
            {addLabel}
          </DsButton>
        ) : null}
        {!product.inStock ? <span className="cc-productCard__stockTag">{t("Out of stock")}</span> : null}
      </div>
      <div className="cc-productCard__body">
        {product.brand ? <span className="cc-productCard__brand">{product.brand}</span> : null}
        <Link to={product.url} className="cc-productCard__name" onClick={onNavigate}>{product.nume}</Link>
        <ProductPrice
          pretEuro={product.pretEuro}
          pretLei={product.pretLei}
          pricesHidden={pricesHidden}
          className="cc-productCard__price bx-productCard__price"
        />
        {!compact && showAdd ? (
          <DsButton variant="outline" size="sm" fullWidth className="cc-productCard__addBtn" onClick={onAdd} disabled={isAdding(product.id)}>
            {addLabel}
          </DsButton>
        ) : null}
      </div>
    </article>
  );
}
